import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useBrands } from "@/data/hooks";
import { FadeUp } from "@/components/Reveal";

const CONTACT_EMAIL = "hello@nanda-sa.com";

// Étape 6. Motifs B2B renforcés pour la conversion
const SUBJECTS = [
 "Devis",
 "Distribution",
 "Retail",
 "Rendez-vous",
 "Catalogue",
 "Photos",
 "Vidéos",
 "Institutionnel",
 "Investissement",
 "Partenariat",
 "Presse",
 "Recrutement",
 "Autre",
];

export default function Contact() {
 const { locale } = useLanguage();
 const BRANDS = useBrands();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 const [params] = useSearchParams;
 const preBrand = params.get("brand") || "";
 const preSubject = params.get("subject") || "";

 const [form, setForm] = useState({
 name: "",
 email: "",
 company: "",
 brand: preBrand,
 subject: preSubject,
 message: "",
 });
 const [submitting, setSubmitting] = useState(false);
 const [submitted, setSubmitted] = useState(false);

 const update = (k) => (e) => setForm((f) => ({...f, [k]: e.target.value }));

 const onSubmit = (e) => {
 e.preventDefault;
 if (!form.name || !form.email || !form.message) {
 toast.error(l("Champs requis manquants", "Required fields missing", "Campos obrigatórios em falta"));
 return;
 }
 setSubmitting(true);

 const brandLabel = form.brand ? (BRANDS.find((b) => b.slug === form.brand)?.name || form.brand) : l("Le Groupe", "The Group", "O Grupo");
 const subjectLine = `[NANDA SÁ] ${form.subject || l("Contact", "Contact", "Contacto")}, ${brandLabel}`;
 const bodyLines = [
 `${l("Nom", "Name", "Nome")}: ${form.name}`,
 `Email: ${form.email}`,
 form.company ? `${l("Société", "Company", "Empresa")}: ${form.company}` : null,
 `${l("Marque concernée", "Relevant brand", "Marca em causa")}: ${brandLabel}`,
 form.subject ? `${l("Motif", "Subject", "Motivo")}: ${form.subject}` : null,
 "",
 `${l("Message", "Message", "Mensagem")}:`,
 form.message,
 ].filter(Boolean);

 const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
 window.location.href = mailto;

 setTimeout(() => {
 setSubmitted(true);
 toast.success(l("Votre client mail vient de s'ouvrir. Envoyez le message pour finaliser.", "Your mail client just opened. Send the message to complete.", "O seu cliente de e-mail acaba de abrir. Envie a mensagem para concluir."));
 setSubmitting(false);
 }, 400);
 };

 return (
 <div data-testid="page-contact">
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-20 md:pb-28 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">Contact</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">{l("Écrivez-nous.", "Write to us.", "Escreva-nos.")}</h1>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l("Nous lisons chaque message. Indiquez la marque concernée et le motif si votre demande est spécifique.", "We read every message. Please indicate the relevant brand and subject if your enquiry is specific.", "Lemos cada mensagem. Indique a marca em causa e o motivo se o seu pedido for específico.")}
 </p>
 </FadeUp>
 </div>
 </section>

 <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
 <div className="md:col-span-5 space-y-10">
 <FadeUp>
 <div className="space-y-10">
 <div>
 <div className="label-overline text-nanda-muted mb-4 inline-flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> {l("Implantations", "Locations", "Implantações")}</div>
 <ul className="space-y-2 text-nanda-ink text-lg">
 <li data-testid="contact-implant-canchungo">Canchungo <span className="text-nanda-muted">· Guinée-Bissau</span></li>
 
 </ul>
 </div>
 <div>
 <div className="label-overline text-nanda-muted mb-4 inline-flex items-center gap-2"><Mail size={14} strokeWidth={1.5} /> {l("E-mail", "E-mail", "E-mail")}</div>
 <a href="mailto:hello@nanda-sa.com" data-testid="contact-email-link" className="block text-nanda-ink text-lg hover:text-nanda-terracotta transition-colors">hello@nanda-sa.com</a>
 </div>
 <div>
 <div className="label-overline text-nanda-muted mb-4 inline-flex items-center gap-2"><MessageCircle size={14} strokeWidth={1.5} /> {l("Téléphone & WhatsApp", "Phone & WhatsApp", "Telefone e WhatsApp")}</div>
 <ul className="space-y-2 text-nanda-ink text-lg">
 <li>
 <span className="text-nanda-muted">Guinée-Bissau : </span>
 <a href="https://wa.me/245956828351" target="_blank" rel="noopener noreferrer" data-testid="contact-phone-gw" className="hover:text-nanda-terracotta transition-colors">+245 956 828 351</a>
 </li>
 </ul>
 </div>
 </div>
 </FadeUp>
 </div>

 <FadeUp delay={0.1} className="md:col-span-7">
 {submitted ? (
 <div data-testid="contact-success" className="border border-nanda-line p-10 md:p-14 bg-nanda-surface">
 <div className="label-overline text-nanda-terracotta">✓</div>
 <p className="mt-6 font-serif text-3xl md:text-4xl leading-tight text-nanda-ink">{l("Votre client mail vient de s'ouvrir. Envoyez le message pour finaliser.", "Your mail client just opened. Send the message to complete.", "O seu cliente de e-mail acaba de abrir. Envie a mensagem para concluir.")}</p>
 <a href={`mailto:${CONTACT_EMAIL}`} className="mt-8 inline-flex items-center gap-3 text-nanda-ink hover:text-nanda-terracotta transition-colors label-overline border-b border-nanda-line pb-1" data-testid="contact-mailto-fallback">
 <Mail size={14} strokeWidth={1.5} /> {CONTACT_EMAIL}
 </a>
 </div>
 ) : (
 <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10" noValidate>
 <Field id="name" label={l("Nom", "Name", "Nome")} value={form.name} onChange={update("name")} required testId="contact-name" />
 <Field id="email" type="email" label="Email" value={form.email} onChange={update("email")} required testId="contact-email" />
 <Field id="company" label={l("Société (optionnel)", "Company (optional)", "Empresa (opcional)")} value={form.company} onChange={update("company")} testId="contact-company" />

 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
 <div>
 <label htmlFor="brand" className="label-overline text-nanda-muted block mb-3">{l("Marque concernée", "Relevant brand", "Marca em causa")}</label>
 <select id="brand" value={form.brand} onChange={update("brand")} data-testid="contact-brand" className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink outline-none focus:border-nanda-ink transition-colors">
 <option value="">{l("Le Groupe", "The Group", "O Grupo")}</option>
 {BRANDS.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
 </select>
 </div>
 <div>
 <label htmlFor="subject" className="label-overline text-nanda-muted block mb-3">{l("Motif", "Subject", "Motivo")}</label>
 <select id="subject" value={form.subject} onChange={update("subject")} data-testid="contact-subject" className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink outline-none focus:border-nanda-ink transition-colors">
 <option value="">{l("Choisir un motif", "Choose a subject", "Escolher um motivo")}</option>
 {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
 </select>
 </div>
 </div>

 <div>
 <label htmlFor="message" className="label-overline text-nanda-muted block mb-3">{l("Message", "Message", "Mensagem")}<span className="text-nanda-terracotta ml-1">*</span></label>
 <textarea id="message" rows={5} value={form.message} onChange={update("message")} required data-testid="contact-message" className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink outline-none focus:border-nanda-ink transition-colors resize-none" />
 </div>

 <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-3 bg-nanda-ink text-nanda-bg px-8 py-4 hover:bg-nanda-inkSoft transition-colors duration-500 label-overline disabled:opacity-50">
 <Mail size={14} strokeWidth={1.5} />
 {submitting ? l("Ouverture…", "Opening…", "A abrir…") : l("Envoyer par email", "Send by email", "Enviar por e-mail")}
 </button>
 <p className="mt-4 text-xs text-nanda-muted">{l("Votre client mail s'ouvrira avec le message pré-rempli vers", "Your email client will open with the pre-filled message to", "O seu cliente de e-mail abrirá com a mensagem pré-preenchida para")} <span className="text-nanda-ink">{CONTACT_EMAIL}</span>.</p>
 </form>
 )}
 </FadeUp>
 </section>

 {/* Carte stylisée */}
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <FadeUp className="mb-12">
 <div className="label-overline text-nanda-muted mb-4">{l("Nos implantations", "Our locations", "As nossas implantações")}</div>
 <h2 className="font-serif text-4xl md:text-5xl">{l("Un ancrage.", "One anchor.", "Uma âncora.")}</h2>
 <p className="mt-4 text-nanda-inkSoft text-base md:text-[17px] max-w-2xl">{l("Guinée-Bissau · depuis 2023, au cœur de l'Afrique de l'Ouest.", "Guinea-Bissau · since 2023, at the heart of West Africa.", "Guiné-Bissau · desde 2023, no coração da África Ocidental.")}</p>
 </FadeUp>
 <div className="aspect-[16/7] w-full overflow-hidden border border-nanda-line bg-nanda-bgAlt">
 <iframe
 title="Carte NANDA SÁ"
 src="https://www.openstreetmap.org/export/embed.html?bbox=-16.8%2C11.6%2C-15.4%2C13.0&amp;layer=mapnik"
 className="w-full h-full"
 style={{ filter: "grayscale(0.6) contrast(0.95)" }}
 loading="lazy"
 />
 </div>
 </div>
 </section>
 </div>
 );
}

const Field = ({ id, label, value, onChange, type = "text", required, testId }) => (
 <div>
 <label htmlFor={id} className="label-overline text-nanda-muted block mb-3">
 {label}{required && <span className="text-nanda-terracotta ml-1">*</span>}
 </label>
 <input id={id} type={type} value={value} onChange={onChange} required={required} data-testid={testId} className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink outline-none focus:border-nanda-ink transition-colors" />
 </div>
);
